import {
  MLTaskStatus,
  TrainingTask,
  ValidationTaskResponse,
} from "../../../../vl-api/vlSchemas";

export type JobKind = "TRAIN" | "VALIDATE";

export type ModelJob = {
  id: string;
  kind: JobKind;
  status: MLTaskStatus;
  modelName: string;
  progress: number;
  createdAt: string;
  updatedAt: string;
  error?: { [key: string]: any } | null;
};

const ACTIVE_STATUSES: MLTaskStatus[] = ["INIT", "IN_PROGRESS"];

export const isActiveTask = (task?: { status: MLTaskStatus }) =>
  !!task && ACTIVE_STATUSES.includes(task.status);

export const KIND_TEXT: Record<JobKind, string> = {
  TRAIN: "Training",
  VALIDATE: "Validation",
};

export const toModelJob =
  (kind: JobKind) =>
  (task: TrainingTask | ValidationTaskResponse): ModelJob => ({
    id: task.id,
    kind,
    status: task.status,
    modelName: task.model_name,
    progress: task.progress ?? 0,
    createdAt: task.created_at,
    updatedAt: task.updated_at,
    error: task.error,
  });

// Backend timestamps may be naive UTC (no offset)
export const parseUtcDate = (value: string) =>
  new Date(/([zZ]|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`);

// Opens the matching Model Catalog tab, filtered to the dataset the job ran on
export const getModelCatalogPath = (kind: JobKind, datasetId: string) => {
  const params = new URLSearchParams();
  if (kind === "VALIDATE") {
    params.set("tab", "validation_jobs");
  }
  params.set("dataset_id", datasetId);
  return `/models-catalog?${params.toString()}`;
};

const TASK_MANAGER_TYPE: Record<JobKind, string> = {
  TRAIN: "TRAINING",
  VALIDATE: "MODEL_VALIDATION",
};

// Task Manager keeps the full error of failed jobs; open it filtered to this one's kind
export const getFailedTasksPath = (kind: JobKind, datasetId: string) => {
  const params = new URLSearchParams({
    dataset_id: datasetId,
    task_type: TASK_MANAGER_TYPE[kind],
    task_status: "FAILED",
  });
  return `/task-manager?${params.toString()}`;
};

export const RESULT_ACTION: Record<JobKind, { label: string; hint: string }> = {
  TRAIN: {
    label: "View report",
    hint: "Under Training Jobs in Model Catalog",
  },
  VALIDATE: {
    label: "View report",
    hint: "Under Validation Jobs in Model Catalog",
  },
};

export const getErrorText = (error: ModelJob["error"]) =>
  error ? String(error.message ?? Object.values(error)[0] ?? "") : "";

export const toPercent = (progress: number) => Math.floor(progress * 100);
